import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { toast, Toaster } from "sonner";
import {
  LogOut,
  Plus,
  Trash2,
  Pencil,
  Upload,
  FileText,
  Image as ImageIcon,
  Loader2,
} from "lucide-react";

// ---- helpers ----
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function uploadFile(file, kind = "file") {
  const fd = new FormData();
  fd.append("file", file);
  try {
    const { data } = await api.post("/files/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      maxContentLength: 20 * 1024 * 1024,
      maxBodyLength: 20 * 1024 * 1024,
    });
    // return absolute URL so <img> / <a> work directly
    return `${BACKEND_URL}${data.url}`;
  } catch (err) {
    const detail = err?.response?.data?.detail;
    const msg = formatApiError(detail) || `Failed to upload ${kind}.`;
    throw new Error(msg);
  }
}

// ---- Banner Form ----
function BannerForm({ initial, onSaved, onClose }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    subtitle: initial?.subtitle || "",
    cta_label: initial?.cta_label || "",
    cta_link: initial?.cta_link || "",
    image_base64: initial?.image_base64 || "",
    order: initial?.order ?? 0,
    active: initial?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) {
      toast.error("Image must be ≤ 15MB");
      return;
    }
    setUploading(true);
    try {
      const url = await uploadFile(f, "image");
      setForm((s) => ({ ...s, image_base64: url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image_base64) {
      toast.error("Banner image is required");
      return;
    }
    setSaving(true);
    try {
      if (initial?.id) {
        await api.put(`/banners/${initial.id}`, form);
        toast.success("Banner updated");
      } else {
        await api.post("/banners", form);
        toast.success("Banner created");
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="banner-form">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-widest">Title</Label>
          <Input
            data-testid="banner-title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="mt-1 bg-white/5 border-white/10 text-white"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Order</Label>
          <Input
            type="number"
            data-testid="banner-order"
            value={form.order}
            onChange={(e) =>
              setForm({ ...form, order: Number(e.target.value) })
            }
            className="mt-1 bg-white/5 border-white/10 text-white"
          />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest">Subtitle</Label>
        <Input
          data-testid="banner-subtitle"
          value={form.subtitle}
          onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
          className="mt-1 bg-white/5 border-white/10 text-white"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-widest">CTA Label</Label>
          <Input
            value={form.cta_label}
            onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
            className="mt-1 bg-white/5 border-white/10 text-white"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">CTA Link</Label>
          <Input
            value={form.cta_link}
            onChange={(e) => setForm({ ...form, cta_link: e.target.value })}
            className="mt-1 bg-white/5 border-white/10 text-white"
          />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest mb-2 block">
          Banner Image
        </Label>
        <div className="flex items-center gap-4">
          {form.image_base64 && (
            <img
              src={form.image_base64}
              alt="preview"
              className="w-24 h-16 object-cover border border-white/20"
            />
          )}
          <label className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {uploading ? "Uploading…" : "Upload"}
            <input
              type="file"
              accept="image/*"
              onChange={onFile}
              className="hidden"
              data-testid="banner-image-input"
              disabled={uploading}
            />
          </label>
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-white/10 pt-4">
        <label className="flex items-center gap-2 text-sm">
          <Switch
            checked={form.active}
            onCheckedChange={(v) => setForm({ ...form, active: v })}
            data-testid="banner-active"
          />
          <span className="text-white/70">Active</span>
        </label>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="text-white/70"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            data-testid="banner-save"
            disabled={saving}
            className="bg-gold text-navy hover:bg-white"
          >
            {saving && <Loader2 size={14} className="animate-spin mr-2" />}
            Save
          </Button>
        </div>
      </div>
    </form>
  );
}

// ---- Product Form ----
function ProductForm({ initial, onSaved, onClose }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    category: initial?.category || "BABUTEX",
    description: initial?.description || "",
    image_base64: initial?.image_base64 || "",
    pdf_base64: initial?.pdf_base64 || "",
    order: initial?.order ?? 0,
    active: initial?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const onImg = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) {
      toast.error("Image must be ≤ 15MB");
      return;
    }
    setUploadingImg(true);
    try {
      const url = await uploadFile(f, "image");
      setForm((s) => ({ ...s, image_base64: url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploadingImg(false);
      e.target.value = "";
    }
  };
  const onPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) {
      toast.error("PDF must be ≤ 15MB");
      return;
    }
    setUploadingPdf(true);
    try {
      const url = await uploadFile(f, "PDF");
      setForm((s) => ({ ...s, pdf_base64: url }));
      toast.success("PDF uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploadingPdf(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.image_base64) {
      toast.error("Name and image are required");
      return;
    }
    setSaving(true);
    try {
      if (initial?.id) {
        await api.put(`/products/${initial.id}`, form);
        toast.success("Product updated");
      } else {
        await api.post("/products", form);
        toast.success("Product created");
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="product-form">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-widest">Name</Label>
          <Input
            data-testid="product-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-1 bg-white/5 border-white/10 text-white"
            required
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Category</Label>
          <Input
            data-testid="product-category"
            value={form.category}
            onChange={(e) =>
              setForm({ ...form, category: e.target.value.toUpperCase() })
            }
            className="mt-1 bg-white/5 border-white/10 text-white"
          />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest">Description</Label>
        <Textarea
          data-testid="product-description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={3}
          className="mt-1 bg-white/5 border-white/10 text-white"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-widest mb-2 block">
            Product Image
          </Label>
          <div className="flex items-center gap-3">
            {form.image_base64 && (
              <img
                src={form.image_base64}
                alt="preview"
                className="w-16 h-16 object-cover border border-white/20"
              />
            )}
            <label className={`cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-white/20 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors ${uploadingImg ? "opacity-60 pointer-events-none" : ""}`}>
              {uploadingImg ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />}
              {uploadingImg ? "Uploading…" : "Choose"}
              <input
                type="file"
                accept="image/*"
                onChange={onImg}
                className="hidden"
                data-testid="product-image-input"
                disabled={uploadingImg}
              />
            </label>
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest mb-2 block">
            PDF Catalog
          </Label>
          <div className="flex items-center gap-3">
            {form.pdf_base64 && (
              <span className="text-xs text-emerald-400 flex items-center gap-1">
                <FileText size={12} /> attached
              </span>
            )}
            <label className={`cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-white/20 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors ${uploadingPdf ? "opacity-60 pointer-events-none" : ""}`}>
              {uploadingPdf ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              {uploadingPdf ? "Uploading…" : "Upload PDF"}
              <input
                type="file"
                accept="application/pdf"
                onChange={onPdf}
                className="hidden"
                data-testid="product-pdf-input"
                disabled={uploadingPdf}
              />
            </label>
            {form.pdf_base64 && (
              <button
                type="button"
                onClick={() => setForm({ ...form, pdf_base64: "" })}
                className="text-white/50 hover:text-red-400 text-xs"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-widest">Order</Label>
        <Input
          type="number"
          value={form.order}
          onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
          className="mt-1 bg-white/5 border-white/10 text-white w-32"
        />
      </div>

      <div className="flex items-center justify-between border-t border-white/10 pt-4">
        <label className="flex items-center gap-2 text-sm">
          <Switch
            checked={form.active}
            onCheckedChange={(v) => setForm({ ...form, active: v })}
          />
          <span className="text-white/70">Active</span>
        </label>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            data-testid="product-save"
            disabled={saving}
            className="bg-gold text-navy hover:bg-white"
          >
            {saving && <Loader2 size={14} className="animate-spin mr-2" />}
            Save
          </Button>
        </div>
      </div>
    </form>
  );
}

// ---- Settings Form ----
function SettingsPanel({ settings, onSaved }) {
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(settings || {}), [settings]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { id, ...body } = form;
      await api.put("/settings", body);
      toast.success("Settings saved");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const F = (name, label, type = "text") => (
    <div key={name}>
      <Label className="text-xs uppercase tracking-widest">{label}</Label>
      <Input
        data-testid={`settings-${name}`}
        value={form?.[name] || ""}
        onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        className="mt-1 bg-white/5 border-white/10 text-white"
        type={type}
      />
    </div>
  );

  return (
    <form onSubmit={submit} className="max-w-2xl space-y-5" data-testid="settings-form">
      <div className="grid md:grid-cols-2 gap-4">
        {F("brand_name", "Brand name")}
        {F("tagline", "Tagline")}
        {F("phone", "Phone")}
        {F("whatsapp", "WhatsApp (digits, incl. country code)")}
        {F("email", "Email", "email")}
        {F("address", "Address")}
        {F("instagram", "Instagram URL")}
        {F("facebook", "Facebook URL")}
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest">About</Label>
        <Textarea
          value={form?.about || ""}
          onChange={(e) => setForm({ ...form, about: e.target.value })}
          rows={4}
          className="mt-1 bg-white/5 border-white/10 text-white"
        />
      </div>
      <Button
        type="submit"
        data-testid="settings-save"
        disabled={saving}
        className="bg-gold text-navy hover:bg-white"
      >
        {saving && <Loader2 size={14} className="animate-spin mr-2" />}
        Save settings
      </Button>
    </form>
  );
}

// ---- Main dashboard ----
export default function AdminDashboard() {
  const { user, checking, logout } = useAuth();
  const nav = useNavigate();
  const [banners, setBanners] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({});
  const [editBanner, setEditBanner] = useState(null);
  const [openBannerDlg, setOpenBannerDlg] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [openProductDlg, setOpenProductDlg] = useState(false);

  useEffect(() => {
    if (!checking && !user) nav("/admin/login", { replace: true });
  }, [checking, user, nav]);

  const load = async () => {
    const [b, p, s] = await Promise.all([
      api.get("/banners"),
      api.get("/products"),
      api.get("/settings"),
    ]);
    setBanners(b.data || []);
    setProducts(p.data || []);
    setSettings(s.data || {});
  };
  useEffect(() => {
    if (user) load();
  }, [user]);

  const deleteBanner = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    await api.delete(`/banners/${id}`);
    toast.success("Deleted");
    load();
  };
  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  if (checking)
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center text-white/60">
        <Loader2 className="animate-spin" />
      </div>
    );
  if (!user) return null;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-navy text-white">
      <Toaster theme="dark" position="top-right" />

      {/* header bar */}
      <header className="border-b border-white/10 bg-navy/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-lg">
            B'Premium <span className="text-white/40 text-sm">/ Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-xs text-white/50 hidden md:inline">
              {user.email}
            </span>
            <button
              data-testid="admin-logout"
              onClick={() => {
                logout();
                nav("/admin/login");
              }}
              className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-white/70 hover:text-gold transition-colors"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 md:px-10 py-8 md:py-12">
        <Tabs defaultValue="products" className="w-full">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="banners" data-testid="tab-banners">
              Banners ({banners.length})
            </TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">
              Products ({products.length})
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Banners */}
          <TabsContent value="banners" className="mt-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="chapter-num mb-1">— Hero</p>
                <h2 className="font-display text-2xl md:text-3xl">
                  Home banners
                </h2>
              </div>
              <Dialog open={openBannerDlg} onOpenChange={setOpenBannerDlg}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="add-banner-btn"
                    onClick={() => setEditBanner(null)}
                    className="bg-gold text-navy hover:bg-white"
                  >
                    <Plus size={14} className="mr-1" /> New banner
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-navy-800 border-white/10 max-w-xl">
                  <DialogHeader>
                    <DialogTitle className="font-display text-2xl">
                      {editBanner ? "Edit banner" : "New banner"}
                    </DialogTitle>
                  </DialogHeader>
                  <BannerForm
                    initial={editBanner}
                    onSaved={load}
                    onClose={() => setOpenBannerDlg(false)}
                  />
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {banners.map((b) => (
                <div
                  key={b.id}
                  data-testid={`banner-row-${b.id}`}
                  className="border border-white/10 bg-white/[0.02]"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={b.image_base64}
                      alt={b.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs text-white/40 uppercase tracking-widest">
                          #{b.order} · {b.active ? "Live" : "Hidden"}
                        </p>
                        <h3 className="font-display text-lg mt-1">
                          {b.title || "Untitled"}
                        </h3>
                        <p className="text-white/50 text-sm line-clamp-1">
                          {b.subtitle}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setEditBanner(b);
                            setOpenBannerDlg(true);
                          }}
                          className="w-8 h-8 border border-white/15 flex items-center justify-center hover:border-gold hover:text-gold"
                          aria-label="Edit"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          data-testid={`delete-banner-${b.id}`}
                          onClick={() => deleteBanner(b.id)}
                          className="w-8 h-8 border border-white/15 flex items-center justify-center hover:border-red-400 hover:text-red-400"
                          aria-label="Delete"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Products */}
          <TabsContent value="products" className="mt-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="chapter-num mb-1">— Catalog</p>
                <h2 className="font-display text-2xl md:text-3xl">Products</h2>
              </div>
              <Dialog open={openProductDlg} onOpenChange={setOpenProductDlg}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="add-product-btn"
                    onClick={() => setEditProduct(null)}
                    className="bg-gold text-navy hover:bg-white"
                  >
                    <Plus size={14} className="mr-1" /> New product
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-navy-800 border-white/10 max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="font-display text-2xl">
                      {editProduct ? "Edit product" : "New product"}
                    </DialogTitle>
                  </DialogHeader>
                  <ProductForm
                    initial={editProduct}
                    onSaved={load}
                    onClose={() => setOpenProductDlg(false)}
                  />
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map((p) => (
                <div
                  key={p.id}
                  data-testid={`product-row-${p.id}`}
                  className="border border-white/10 bg-white/[0.02]"
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={p.image_base64}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-3">
                    <p className="text-[10px] text-gold uppercase tracking-widest">
                      {p.category}
                    </p>
                    <h3 className="font-display text-base mt-1 line-clamp-1">
                      {p.name}
                    </h3>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[10px] text-white/40 uppercase tracking-widest">
                        {p.pdf_base64 ? "PDF" : "no pdf"} · {p.active ? "Live" : "Off"}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setEditProduct(p);
                            setOpenProductDlg(true);
                          }}
                          className="w-7 h-7 border border-white/15 flex items-center justify-center hover:border-gold hover:text-gold"
                          aria-label="Edit"
                        >
                          <Pencil size={11} />
                        </button>
                        <button
                          data-testid={`delete-product-${p.id}`}
                          onClick={() => deleteProduct(p.id)}
                          className="w-7 h-7 border border-white/15 flex items-center justify-center hover:border-red-400 hover:text-red-400"
                          aria-label="Delete"
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <p className="chapter-num mb-1">— Contact & brand</p>
            <h2 className="font-display text-2xl md:text-3xl mb-6">
              Site settings
            </h2>
            <SettingsPanel settings={settings} onSaved={load} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
